import moment from 'moment';
import React, { useEffect, useState } from 'react'
import { db } from "../config/firebase";
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import "../TeeTimes.css";
import {
    buildSlotOccupancy,
    evaluateSlotAvailability,
    isSlotBlocked,
    passesBaseCapacityFilter,
    passesJoinOnlyFilter,
} from '../utils/teeSlotCapacity';
import SuperadminSlotDebugPanel from './SuperadminSlotDebugPanel';

/** Tee sheet `blockedTimes[].time` may be stored as an array, a single string, or a map. */
function blockedTimeSlots(time: unknown): string[] {
    if (time == null) return [];
    if (Array.isArray(time)) return time.filter((t): t is string => typeof t === 'string' && t.length > 0);
    if (typeof time === 'string' && time.length > 0) return [time];
    if (typeof time === 'object') {
        return Object.values(time as Record<string, unknown>).filter(
            (t): t is string => typeof t === 'string' && t.length > 0
        );
    }
    return [];
}

/** Tee sheet `blockedTimes[].days` may be an array or occasionally a single day string. */
function blockedDays(days: unknown): string[] {
    if (days == null) return [];
    if (Array.isArray(days)) return days.filter((d): d is string => typeof d === 'string' && d.length > 0);
    if (typeof days === 'string' && days.length > 0) return [days];
    if (typeof days === 'object') {
        return Object.values(days as Record<string, unknown>).filter(
            (d): d is string => typeof d === 'string' && d.length > 0
        );
    }
    return [];
}

function isTimeInBlockRange(time: string, startTime: string, endTime: string): boolean {
    const t = moment(time, 'h:mm a').format('HH:mm');
    const s = moment(startTime, 'h:mm a').format('HH:mm');
    const e = moment(endTime, 'h:mm a').format('HH:mm');
    return t >= s && t <= e;
}

function getTeetimeGroupBlockReason(
    time: string,
    teeSheetId: string,
    groups: Array<{
        date?: string;
        blockStartTime?: string;
        blockEndTime?: string;
        blockTitle?: string;
        blockTeeID?: string;
    }>,
    reservationDate: string
): string | null {
    const targetDate = moment(reservationDate).format('YYYY-MM-DD');
    for (const group of groups) {
        if (!group?.blockStartTime || !group?.blockEndTime || !group.blockTitle) continue;
        if (group.blockTeeID !== teeSheetId) continue;
        if (moment(group?.date).format('YYYY-MM-DD') !== targetDate) continue;
        if (isTimeInBlockRange(time, group.blockStartTime, group.blockEndTime)) {
            return `Course block: ${group.blockTitle} (${group.blockStartTime}–${group.blockEndTime})`;
        }
    }
    return null;
}

function getRecurringBlockReason(
    time: string,
    teeSheet: { blockedTimes?: Array<{ days?: unknown; time?: unknown }> },
    reservationDate: string
): string | null {
    const dayName = moment(reservationDate).format('dddd');
    for (const blockedTime of teeSheet.blockedTimes || []) {
        if (!blockedDays(blockedTime?.days).includes(dayName)) continue;
        if (blockedTimeSlots(blockedTime?.time).includes(time)) {
            return 'Recurring blocked time for this day of week';
        }
    }
    return null;
}

function AvailableTimes({ reservation, updateReservation, date, widgetSettings, widgetRules, setPageLoaderStatus, isSuperAdmin = false }) {
    console.log('AvailableTimes component rendering with props:', {
        reservation,
        date,
        widgetSettings,
        widgetRules
    });

    const [teeTimeGroups, setTeeTimeGroups] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [teeSheets, setTeeSheets] = useState([]);
    const [allow18Holes, setAllow18Holes] = useState([]);
    const [allow9Holes, setAllow9Holes] = useState([]);
    const [minGroupNotMet, setMinGroupNotMet] = useState(false);
    const [debugSlots, setDebugSlots] = useState([]);
    const groupID = localStorage.getItem('group');
    const courseID = localStorage.getItem('course');

    // Add logging for initial mount and state changes
    useEffect(() => {
        console.log('AvailableTimes component mounted');
        return () => console.log('AvailableTimes component unmounted');
    }, []);

    // Log state changes
    useEffect(() => {
        console.log('teeTimeGroups state changed:', {
            length: teeTimeGroups.length,
            data: teeTimeGroups
        });
    }, [teeTimeGroups]);

    useEffect(() => {
        console.log('teeSheets state changed:', {
            length: teeSheets.length,
            data: teeSheets
        });
    }, [teeSheets]);

    console.log('AvailableTimes Component Props:', {
        reservation,
        date,
        widgetSettings,
        widgetRules,
        groupID,
        courseID
    });

    // Create references for Firestore collections
    const teeTimeGroupsRef = collection(db, 'groups', groupID, 'courses', courseID, 'teetimegroups');
    const teeSheetsRef = collection(db, 'groups', groupID, 'courses', courseID, 'teesheets');

    // Effect to set up tee sheets subscription
    useEffect(() => {
        console.log('Setting up tee sheets subscription');
        const unsubscribeTeeSheets = onSnapshot(
            teeSheetsRef,
            (snapshot) => {
                console.log('Tee sheets snapshot received:', {
                    empty: snapshot.empty,
                    size: snapshot.size,
                    docs: snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                });
                const mappedData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                console.log('Setting teeSheets state with:', mappedData);
                setTeeSheets(mappedData);
            },
            (err) => {
                console.error('Error getting tee sheets:', err);
            }
        );

        return () => {
            console.log('Cleaning up tee sheets subscription');
            unsubscribeTeeSheets();
        };
    }, []);

    useEffect(() => {
        setAllow18Holes(widgetSettings?.allow18Holes);
        setAllow9Holes(widgetSettings?.allow9Holes);
        console.log('Widget Settings Updated:', {
            allow18Holes: widgetSettings?.allow18Holes,
            allow9Holes: widgetSettings?.allow9Holes
        });
        //eslint-disable-next-line
    }, [widgetSettings]);

    const generateTimeslots = (timeInterval, startTime, endTime) => {
        console.log('Generating timeslots:', { timeInterval, startTime, endTime });
        startTime = moment(startTime, 'h:mm a');
        endTime = moment(endTime, 'h:mm a');

        let slotArray = [];
        slotArray.push(startTime.format('h:mm a'));

        let newTime = startTime;
        while (startTime.isBefore(endTime)) {
            newTime = newTime.add(timeInterval, 'minutes');
            slotArray.push(newTime.format('h:mm a'));
        }

        console.log('Generated timeslots:', slotArray);
        return slotArray;
    }

    function removeFromArray(arr, value) {
        console.log('Removing value from array:', { value, arrayLength: arr.length });
        const filtered = arr.filter(function (ele) {
            return ele !== value;
        });
        console.log('Array after removal:', { newLength: filtered.length });
        return filtered;
    }

    // This is used to remove any times from the array of available times
    // that conflict with the course's specific widget rules for any given day
    function filterWidgetRules(uniqueTeeTimes) {
        console.log('Filtering widget rules for times:', uniqueTeeTimes);
        let today = moment().format('dddd');
        let resDay = moment(reservation.date).format('dddd');
        let now = moment().format('h:mm a');
        console.log('Current context:', { today, resDay, now });

        // Reset minGroupNotMet at the start of filtering
        setMinGroupNotMet(false);

        uniqueTeeTimes?.forEach((time, i) => {
            let useDefaultSettings = true;
            let shouldRemove = false;
            console.log('Processing time:', time);

            // First apply any active rules to this time
            widgetRules?.forEach(rule => {
                if (rule.active && rule.when?.days?.includes(resDay)) {
                    console.log('Checking rule:', rule);
                    if (
                        moment(time.time, 'h:mm a') >= moment(rule.startTime, 'h:mm a')
                        && moment(time.time, 'h:mm a') <= moment(rule.endTime, 'h:mm a')) {

                        console.log('Time falls within rule period:', time.time);

                        // Track effective min group size for later occupancy-based exceptions.
                        if (reservation.numHoles === 18) {
                            time.minGroupSize = rule.minGroupSize18;
                        } else if (reservation.numHoles === 9) {
                            time.minGroupSize = rule.minGroupSize9;
                        }

                        if (reservation.numHoles === 18 && !rule.allow18Holes.includes(time.teeSheet)) {
                            console.log('18 holes not allowed for this time:', time.time);
                            shouldRemove = true;
                        }
                        if (reservation.numHoles === 9 && !rule.allow9Holes.includes(time.teeSheet)) {
                            console.log('9 holes not allowed for this time:', time.time);
                            shouldRemove = true;
                        }

                        // Singles can only book by joining an already-occupied slot (handled later by occupancy filtering),
                        // so min-group-size rules should not hide joinable times for singles.
                        const isSingle = reservation.numPlayers === 1;
                        const isTwosomeMin3Exception =
                            reservation.numPlayers === 2
                            && ((reservation.numHoles === 18 && rule.minGroupSize18 === 3) || (reservation.numHoles === 9 && rule.minGroupSize9 === 3));

                        if (!isSingle && !isTwosomeMin3Exception) {
                            if (reservation.numHoles === 18 && reservation.numPlayers < rule.minGroupSize18) {
                                console.log('Minimum players not met for 18 holes:', time.time);
                                shouldRemove = true;
                                setMinGroupNotMet(true);
                            } else if (reservation.numHoles === 9 && reservation.numPlayers < rule.minGroupSize9) {
                                console.log('Minimum players not met for 9 holes:', time.time);
                                shouldRemove = true;
                                setMinGroupNotMet(true);
                            }
                        }

                        // Add requirePayment property if rule requires payment
                        if (rule.requirePayment === "always") {
                            console.log('Adding requirePayment property for time:', time.time);
                            time.requirePayment = true;
                        }

                        useDefaultSettings = false;
                    }
                }
            });

            // If no active rules apply, use the default settings
            if (reservation.numHoles === 18 && useDefaultSettings && !allow18Holes.includes(time.teeSheet)) {
                console.log('Removing time due to default 18-hole settings:', time.time);
                shouldRemove = true;
            }

            if (reservation.numHoles === 9 && useDefaultSettings && !allow9Holes.includes(time.teeSheet)) {
                console.log('Removing time due to default 9-hole settings:', time.time);
                shouldRemove = true;
            }

            // Mark for removal instead of deleting immediately
            if (shouldRemove) {
                uniqueTeeTimes[i] = null;
            }
        });

        // Filter out null entries at the end
        const filteredTimes = uniqueTeeTimes.filter(time => time !== null);
        console.log('Times after widget rules:', filteredTimes);
        return filteredTimes;
    }

    function getUniqueTeeTimes(teeSheets, timeSlotArray) {
        console.log('Getting unique tee times:', { teeSheets, timeSlotArray });
        let uniqueTeeTimes = [];

        teeSheets.forEach((teeSheet) => {
            console.log('Processing tee sheet:', teeSheet);
            timeSlotArray[teeSheet.id].forEach((timeslot) => {
                //prevent duplicates on separate tee sheets
                // if the timeslot is at least an hour from now (OR if the reservation date is not today?-Removed 03012022)
                console.log('Processing timeslot:', timeslot);

                if (((reservation.date === date && moment(timeslot, 'h:mm a') > moment().add(widgetSettings.currentDayMinutesInAdvance, 'minutes')) || reservation.date !== date)) {
                    console.log('Timeslot passes initial time check:', timeslot);

                    if (reservation.numHoles === 18) {
                        const reroundTime = moment(timeslot, 'h:mm a').add(teeSheet.roundLength, 'minutes').format('h:mm a');
                        console.log('Checking 18-hole reround time:', reroundTime);

                        if (timeSlotArray[teeSheet.reroundTeeID] && timeSlotArray[teeSheet.reroundTeeID].includes(reroundTime)) {
                            console.log('Adding 18-hole time:', timeslot);
                            uniqueTeeTimes.push({
                                'time': timeslot,
                                'teeSheet': teeSheet.id,
                                'teeSheetName': teeSheet.name,
                                'reroundTee': teeSheet.reroundTeeID,
                                'roundLength': teeSheet.roundLength
                            });
                        } else {
                            console.log('Reround time not available:', reroundTime);
                        }
                    } else if (reservation.numHoles === 9) {
                        console.log('Adding 9-hole time:', timeslot);
                        uniqueTeeTimes.push({
                            'time': timeslot,
                            'teeSheet': teeSheet.id,
                            'teeSheetName': teeSheet.name,
                            'reroundTee': teeSheet.reroundTeeID,
                            'roundLength': teeSheet.roundLength
                        });
                    }
                } else {
                    console.log('Timeslot failed initial time check:', timeslot);
                }
            });
        });

        uniqueTeeTimes.sort((a, b) => (moment(a.time, 'h:mm a').format('HH:mm') < moment(b.time, 'h:mm a').format('HH:mm')) ? -1 : 1);
        console.log('Raw candidate tee times:', uniqueTeeTimes);
        return uniqueTeeTimes;
    }

    // Effect to reset available times when date changes
    useEffect(() => {
        console.log('Date changed effect triggered:', {
            date: reservation.date,
            groupID,
            courseID
        });

        if (!reservation.date) {
            console.log('No reservation date, skipping reset');
            return;
        }

        console.log('Setting up tee time groups query with params:', {
            date: reservation.date,
            groupID,
            courseID,
            path: `groups/${groupID}/courses/${courseID}/teetimegroups`
        });

        const normalizedDate = moment(reservation.date).format('YYYY-MM-DD');
        const teeTimeGroupsQuery = query(teeTimeGroupsRef, where('date', '==', normalizedDate));
        console.log('Tee time groups query created:', teeTimeGroupsQuery);

        const unsubscribeTeeTimeGroups = onSnapshot(
            teeTimeGroupsQuery,
            (snapshot) => {
                console.log('Tee time groups snapshot received:', {
                    empty: snapshot.empty,
                    size: snapshot.size,
                    docs: snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                });
                const mappedData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                console.log('Setting teeTimeGroups state with:', mappedData);
                setTeeTimeGroups(mappedData);
            },
            (err) => {
                console.error('Error getting tee time groups:', err);
            }
        );

        // Get tee sheets for the course
        console.log('Setting up tee sheets query');
        const unsubscribeTeeSheets = onSnapshot(
            teeSheetsRef,
            (snapshot) => {
                console.log('Tee sheets snapshot received:', {
                    empty: snapshot.empty,
                    size: snapshot.size,
                    docs: snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                });
                const mappedData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                console.log('Setting teeSheets state with:', mappedData);
                setTeeSheets(mappedData);
            },
            (err) => {
                console.error('Error getting tee sheets:', err);
            }
        );

        // Cleanup function
        return () => {
            console.log('Cleaning up subscriptions');
            unsubscribeTeeTimeGroups();
            unsubscribeTeeSheets();
        };
    }, [reservation.date]);

    // Effect to generate available times when data changes
    useEffect(() => {
        console.log('Data changed effect triggered:', {
            teeSheetsLength: teeSheets.length,
            teeTimeGroupsLength: teeTimeGroups.length,
            reservationDate: reservation.date,
            reservationNumHoles: reservation.numHoles,
            reservationNumPlayers: reservation.numPlayers  // Add this logging
        });

        if (teeSheets.length > 0) {  // Only check for teeSheets
            console.log('Tee sheets available, proceeding with time generation');
            let timeSlotArray = {};

            const occupancy = buildSlotOccupancy(teeTimeGroups, {
                date: reservation.date,
            });

            // Generate time slots for each tee sheet
            teeSheets.forEach((teeSheet) => {
                console.log('Processing tee sheet:', teeSheet);
                timeSlotArray[teeSheet.id] = generateTimeslots(
                    teeSheet.teeTimeInterval,
                    teeSheet.startTime,
                    teeSheet.endTime
                );

                // Remove blocked times (clubhouse blocks stored as teetimegroups docs).
                // Uses the same predicate as the hold-creation and server-confirmation
                // checks so the widget and the server can never disagree about blocks.
                timeSlotArray[teeSheet.id] = timeSlotArray[teeSheet.id].filter(
                    (time) => !isSlotBlocked(teeTimeGroups, {
                        teeSheetID: teeSheet.id,
                        time,
                        date: reservation.date,
                    })
                );

                // Remove Recurring Times
                teeSheet.blockedTimes?.forEach((blockedTime) => {
                    const dayName = moment(reservation?.date).format('dddd');
                    if (!blockedDays(blockedTime?.days).includes(dayName)) return;
                    blockedTimeSlots(blockedTime?.time).forEach((time) => {
                        timeSlotArray[teeSheet.id] = removeFromArray(timeSlotArray[teeSheet.id], time);
                    });
                });
            });

            const rawCandidates = getUniqueTeeTimes(teeSheets, timeSlotArray);
            const requestedPlayers = Number(reservation?.numPlayers) || 0;
            const numHoles = Number(reservation?.numHoles) || 0;

            const afterBaseCapacity = rawCandidates.filter((t) =>
                passesBaseCapacityFilter({
                    occupancy,
                    slot: t,
                    numPlayers: requestedPlayers,
                    numHoles,
                })
            );

            const afterWidgetRules = filterWidgetRules(afterBaseCapacity);

            const filteredByCapacity = afterWidgetRules.filter((t) =>
                passesJoinOnlyFilter({
                    occupancy,
                    slot: t,
                    numPlayers: requestedPlayers,
                    numHoles,
                })
            );

            console.log('Setting available times:', {
                raw: rawCandidates.length,
                afterBaseCapacity: afterBaseCapacity.length,
                afterWidgetRules: afterWidgetRules.length,
                final: filteredByCapacity.length,
            });
            setAvailableTimes(filteredByCapacity);

            if (
                isSuperAdmin &&
                reservation.date &&
                reservation.numHoles &&
                reservation.numPlayers
            ) {
                const debugRows = [];
                const advanceMins = widgetSettings?.currentDayMinutesInAdvance ?? 0;

                teeSheets.forEach((teeSheet) => {
                    const fullSchedule = generateTimeslots(
                        teeSheet.teeTimeInterval,
                        teeSheet.startTime,
                        teeSheet.endTime
                    );

                    fullSchedule.forEach((timeslot) => {
                        const groupBlockReason = getTeetimeGroupBlockReason(
                            timeslot,
                            teeSheet.id,
                            teeTimeGroups,
                            reservation.date
                        );
                        const onSheet = timeSlotArray[teeSheet.id]?.includes(timeslot) ?? false;
                        let blockedReason = groupBlockReason;
                        if (!blockedReason && !onSheet) {
                            blockedReason = getRecurringBlockReason(
                                timeslot,
                                teeSheet,
                                reservation.date
                            );
                        }

                        const failsAdvanceWindow =
                            reservation.date === date &&
                            !(
                                moment(timeslot, 'h:mm a') >
                                moment().add(advanceMins, 'minutes')
                            );

                        let fails18Reround = false;
                        let reroundTimeLabel = '';
                        if (numHoles === 18) {
                            reroundTimeLabel = moment(timeslot, 'h:mm a')
                                .add(teeSheet.roundLength, 'minutes')
                                .format('h:mm a');
                            fails18Reround = !(
                                timeSlotArray[teeSheet.reroundTeeID] &&
                                timeSlotArray[teeSheet.reroundTeeID].includes(reroundTimeLabel)
                            );
                        }

                        const slot = {
                            time: timeslot,
                            teeSheet: teeSheet.id,
                            teeSheetName: teeSheet.name,
                            reroundTee: teeSheet.reroundTeeID,
                            roundLength: teeSheet.roundLength,
                        };

                        const evaluation = evaluateSlotAvailability({
                            slot,
                            reservation,
                            occupancy,
                            widgetRules,
                            allow18Holes,
                            allow9Holes,
                            timeSlotStillOnSheet: onSheet,
                            blockedReason: blockedReason || undefined,
                            failsAdvanceWindow,
                            fails18Reround: numHoles === 18 ? fails18Reround : false,
                            advanceMinutes: advanceMins,
                            reroundTimeLabel,
                        });

                        debugRows.push({
                            teeSheetId: teeSheet.id,
                            teeSheetName: teeSheet.name,
                            time: timeslot,
                            evaluation,
                        });
                    });
                });

                debugRows.sort((a, b) => {
                    const ta = moment(a.time, 'h:mm a').format('HH:mm');
                    const tb = moment(b.time, 'h:mm a').format('HH:mm');
                    if (ta !== tb) return ta < tb ? -1 : 1;
                    return (a.teeSheetName || '').localeCompare(b.teeSheetName || '');
                });
                setDebugSlots(debugRows);
            } else {
                setDebugSlots([]);
            }
        }
    }, [
        teeSheets,
        teeTimeGroups,
        reservation.date,
        reservation.numHoles,
        reservation.numPlayers,
        isSuperAdmin,
        allow18Holes,
        allow9Holes,
        widgetRules,
        date,
    ]);

    return (
        <div className="p-2">
            <div className="row" id="availableTimes">
                {(!reservation.numHoles || !reservation.numPlayers) && availableTimes.length < 1 &&
                    <p className="text-center">Choose from the options above to see available tee times.</p>
                }
                {reservation.date && reservation.numHoles && reservation.numPlayers && availableTimes.length < 1 &&
                    <p className="text-center">There are no tee times available for these options.</p>
                }
                {reservation.date && reservation.numHoles && reservation.numPlayers && availableTimes.map((thisTime, index) =>
                    <div key={"availableTime" + index} className="col-3 time-slot text-center mb-2">
                        <input
                            type="radio"
                            checked={false}
                            className={thisTime.time !== reservation.time ? "btn-check" : "btn-check btn-primary"}
                            onChange={() => console.log('Selected time of: ')}
                            name="teeTime"
                            id={thisTime.teeSheet + ': ' + thisTime.time}
                            value={thisTime.time}
                            autoComplete="off"
                            data-tee-time={thisTime.time}
                            data-reround-tee={thisTime.reroundTee}
                            data-round-length={thisTime.roundLength}
                            data-tee-sheet={thisTime.teeSheet}
                            data-require-payment={thisTime.requirePayment ? "true" : "false"}
                            onClick={updateReservation}
                        ></input>
                        <label className="btn btn-light" htmlFor={thisTime.teeSheet + ': ' + thisTime.time}>
                            {thisTime.time}
                            {reservation.numHoles === 9 &&
                                <small className="startingTeeName text-secondary" style={{ fontSize: "80%", marginTop: "-1px", display: "block" }}>{thisTime.teeSheetName}</small>
                            }
                        </label>
                    </div>
                )}
                {minGroupNotMet && widgetSettings?.minGroupMessage &&
                    <div className="col-12">
                        <div className="alert alert-warning alert-outline mx-2 text-center">{widgetSettings?.minGroupMessage}</div>
                    </div>
                }
                {isSuperAdmin &&
                    reservation.date &&
                    reservation.numHoles &&
                    reservation.numPlayers && (
                        <SuperadminSlotDebugPanel
                            debugSlots={debugSlots}
                            reservationSummary={`${reservation.numPlayers} player(s), ${reservation.numHoles} holes, ${moment(reservation.date).format('YYYY-MM-DD')} (${moment(reservation.date).format('dddd')})`}
                        />
                    )}
            </div>
        </div>
    )
}

export default AvailableTimes;